import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Camera, LayoutDashboard, LogIn, LogOut } from "lucide-react";
import { api, getToken, setToken } from "../../lib/api";

export default function AppHeader() {
  const [credits, setCredits] = useState(null);
  const [me, setMe] = useState(null);
  const location = useLocation();

  const load = async () => {
    try {
      const { data } = await api.get("/scans/status");
      setCredits(data);
    } catch (e) {
      /* ignore */
    }
    if (getToken()) {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data.user);
      } catch (e) {
        setToken(null);
        setMe(null);
      }
    } else {
      setMe(null);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 6000);
    const onAuth = () => load();
    window.addEventListener("mm-auth-change", onAuth);
    return () => {
      clearInterval(iv);
      window.removeEventListener("mm-auth-change", onAuth);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const remaining = credits ? credits.remaining : "—";
  const freeLeft = credits ? Math.max(0, credits.free_quota - credits.free_used) : 2;
  const paid = credits ? credits.paid_credits : 0;

  return (
    <header
      className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200"
      data-testid="app-header"
    >
      <div className="max-w-md mx-auto flex items-center justify-between px-5 h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="home-link">
          <div className="w-9 h-9 rounded-2xl bg-blue-700 grid place-items-center text-white">
            <Camera size={18} strokeWidth={2.75} />
          </div>
          <div className="leading-none">
            <div className="font-extrabold tracking-tight text-slate-900">
              MarketMate
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
              Valuation engine
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-2">
          <div
            className="hidden xs:flex items-center gap-1 bg-blue-50 border border-blue-100 rounded-full px-3 h-9"
            data-testid="scan-counter"
          >
            <span className="font-mono text-sm font-bold text-blue-800">
              {remaining}
            </span>
            <span className="text-[11px] uppercase tracking-wider text-blue-700/80 font-bold">
              scans
            </span>
          </div>
          {me ? (
            <Link
              to="/dashboard"
              className="w-9 h-9 rounded-full grid place-items-center bg-slate-100 hover:bg-slate-200"
              data-testid="dashboard-link"
              title={me.email}
            >
              <LayoutDashboard size={16} />
            </Link>
          ) : (
            <Link
              to="/auth"
              className="w-9 h-9 rounded-full grid place-items-center bg-slate-100 hover:bg-slate-200"
              data-testid="login-link"
            >
              <LogIn size={16} />
            </Link>
          )}
        </div>
      </div>
      <div
        className="max-w-md mx-auto flex items-center gap-2 px-5 pb-2 text-xs text-slate-500"
        data-testid="scan-counter-detail"
      >
        <span className="font-mono font-semibold text-slate-700">
          {freeLeft}
        </span>
        <span>of {credits ? credits.free_quota : 2} free scans</span>
        <span className="text-slate-300">•</span>
        <span className="font-mono font-semibold text-slate-700">{paid}</span>
        <span>paid scans</span>
        {me && (
          <button
            onClick={() => {
              setToken(null);
              window.location.href = "/";
            }}
            className="ml-auto text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
            data-testid="logout-btn"
          >
            <LogOut size={12} /> Log out
          </button>
        )}
      </div>
    </header>
  );
}
